import FunnelLayout from "@components/quiz/FunnelLayout"
import FunnelLogo from "@components/quiz/FunnelLogo"
import StickyFooter from "@components/quiz/StickyFooter"
import ProgressForecastChart from "@components/quiz/visuals/ProgressForecastChart"
import getPersonalization, {
  toInlineLabel,
} from "@pages/onboarding/personalization"
import { useMemo } from "react"
import { Trans, useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"
import getTargetDateLabel, { formatShortDate } from "./getTargetDate"

export default function PlanReadyPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const personalization = useMemo(() => getPersonalization(t), [t])
  const targetDate = useMemo(getTargetDateLabel, [])

  const summary = [
    {
      label: t("pages.checkout.paywall.summary.goal"),
      value: personalization.goalLabel,
    },
    {
      label: t("pages.checkout.paywall.summary.dailyGoal"),
      value: t("pages.checkout.paywall.summary.minutesPerDay", {
        minutes: personalization.minutes,
      }),
    },
    {
      label: t("pages.checkout.paywall.summary.topics"),
      value: personalization.topicLabels.join(", "),
    },
    {
      label: t("pages.checkout.paywall.summary.learnerType"),
      value: personalization.learnerTypeLabel,
    },
  ].filter((row) => row.value)

  return (
    <FunnelLayout
      header={
        <header className="vm-container flex h-16 items-center justify-center">
          <FunnelLogo />
        </header>
      }
    >
      <div className="flex grow flex-col gap-6 pt-2">
        <div className="animate-fade-up space-y-3">
          <h1 className="vm-heading">
            <Trans
              components={{ colored: <span className="vm-highlight" /> }}
              i18nKey="pages.checkout.paywall.planReady.heading"
            />
          </h1>
          <p className="vm-subheading">
            <Trans
              components={{
                bold: <strong className="font-semibold text-vm-ink" />,
              }}
              i18nKey="pages.checkout.paywall.planReady.subheading"
              values={{
                date: targetDate,
                goal: toInlineLabel(personalization.goalLabel),
              }}
            />
          </p>
        </div>

        <div className="animate-fade-up [animation-delay:150ms]">
          <ProgressForecastChart
            goalLabel={t("pages.onboarding.steps.learning-time.tooltip", {
              count: personalization.mindMapsPerMonth,
            })}
            startLabel={formatShortDate(new Date())}
            targetLabel={targetDate}
          />
        </div>

        <div className="vm-card animate-fade-up divide-y divide-vm-line [animation-delay:300ms]">
          {summary.map((row, index) => (
            <div
              className="flex animate-fade-in items-center justify-between gap-4 px-4 py-3"
              key={row.label}
              style={{ animationDelay: `${1600 + index * 150}ms` }}
            >
              <span className="text-sm text-vm-muted">{row.label}</span>
              <span className="text-right text-sm font-semibold text-vm-ink">
                {row.value}
              </span>
            </div>
          ))}
        </div>

        <StickyFooter>
          <button
            className="vm-button-primary animate-fade-up [animation-delay:1200ms]"
            onClick={() => navigate(`/checkout/plans${location.search}`)}
            type="button"
          >
            {t("shared.continue")}
          </button>
        </StickyFooter>
      </div>
    </FunnelLayout>
  )
}
